import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResolvedPath } from "react-router";

import { EmptyArray, EmptyObject } from "capitalrx-components-2.0/lib/utilities";
import { AuthContext, AuthContextValue, AuthTokenProvider } from "capitalrx-shell-auth";
import classNames from "classnames";
import styles from "./Shell.module.css";

import { AsyncWrapper } from "./AsyncWrapper";
import { ContentLayout } from "./ContentLayout";
import { ErrorPage } from "./ErrorPage";
import { Routes } from "./Routes";
import { ChannelSubscriptionProvider, ChannelSubscriptionTable, DefaultProviderFunction, clearFederationRuntimeCache, fetchAvailableModules, loadModules } from "./api";
import { ModuleDescriptor } from "./api/moduleLoader";
import { ContentChannel } from "./components";
import { DynamicSubscriptionDeregistrationFunc, OwnerModuleContext, ShellContext, ShellContextModel } from "./context";
import { useEvent } from "./hooks";
import { HotkeyRegistryContext, HotkeyRegistryManager, createHotkeyRegistryManager } from "./hotkeyRegistry";
import { ComponentSubscription, ModuleManifest, Subscription } from "./model";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { AlertProvider, OverlayContainer, TooltipContainer, HotKeyRoot } from "capitalrx-components-2.0/lib/general";

const shellHotkeyMetadata = {
  id: "shell",
  name: "Shell",
  permissions: ["hotkeys:register"],
};

type ShellHostWindow = Window & {
  __CAPITAL_RX_MODULE_METADATA__?: unknown;
  __CAPITAL_RX_HOTKEY_REGISTRY__?: HotkeyRegistryManager;
};

/**
 * Wrapper component that conditionally renders HotKeyRoot based on Layer 3 shell configuration.
 * This integrates with the module routing to set metadata and check permissions.
 */
const ShellHotkeyWrapper = ({ children }: { children: ReactNode }): ReactNode => {
  /* Note: In future, extract moduleId from current route
     For now, HotKeyRoot will check metadata set by modules */
  return <HotKeyRoot>{children}</HotKeyRoot>;
};

/** The shell loading state. */
export type LoadingState =
  | "loadAvailableCoreModules"
  | "clearCoreModules"
  | "initializeCoreModules"
  | "loadAvailableAppModules"
  | "clearAppModules"
  | "initializeAppModules"
  | "renderRoutes";

/** The shell life cycle progression table. */
const LifeCycleProcess: Record<LoadingState, LoadingState | null> = {
  loadAvailableCoreModules: "clearCoreModules",
  clearCoreModules: "initializeCoreModules",
  initializeCoreModules: "loadAvailableAppModules",
  loadAvailableAppModules: "clearAppModules",
  clearAppModules: "initializeAppModules",
  initializeAppModules: "renderRoutes",
  renderRoutes: null,
};

/** The shell life cycle names. */
const LoadingStateNames: Record<LoadingState, string> = {
  loadAvailableCoreModules: "Fetch Core Modules",
  clearCoreModules: "Clear Core Modules",
  initializeCoreModules: "Initialize Core Modules",
  loadAvailableAppModules: "Fetch Application Modules",
  clearAppModules: "Clear Application Modules",
  initializeAppModules: "Initialize Application Modules",
  renderRoutes: "Render Routes",
};

/** A handler function for the shell `onLoadingStateChange` event. */
export type ShellLoadingStateChangeHandler = (state: LoadingState) => void;

/** The `Shell` component properties. */
export interface ShellProps {
  /** Event called when the shell loading state changes. */
  readonly onLoadingStateChange?: ShellLoadingStateChangeHandler;

  /** Event Called when the shell analytics ID changes. */
  readonly onAnalyticsIdChange?: (analyticsId: string | null) => void;

  /** The component children. */
  readonly children?: () => ReactNode;
}

/** The main application shell component. */
export const Shell = (props: ShellProps): ReactNode => {
  const { children } = props;

  const path = useResolvedPath("").pathname;
  const loadingCountRef = useRef<number>(0);
  const coreModulePaths = useRef<ReadonlyArray<ModuleDescriptor> | null>(null);
  const appModulePaths = useRef<ReadonlyArray<ModuleDescriptor> | null>(null);
  const coreModules = useRef<ReadonlyArray<ModuleManifest> | null>(null);
  const appModules = useRef<ReadonlyArray<ModuleManifest> | null>(null);

  // Shell-level hotkey registry - single instance for entire shell lifecycle
  const hotkeyRegistryManager = useMemo(() => {
    const manager = createHotkeyRegistryManager();
    // Make registry available globally for cross-module access
    if (typeof window !== "undefined") {
      const hostWindow = window as ShellHostWindow;
      hostWindow.__CAPITAL_RX_HOTKEY_REGISTRY__ = manager;
      if (!hostWindow.__CAPITAL_RX_MODULE_METADATA__) {
        hostWindow.__CAPITAL_RX_MODULE_METADATA__ = shellHotkeyMetadata;
      }
    }
    return manager;
  }, []);

  const deferredPermissions = useRef<ReadonlyArray<string> | null>(null);
  const [locked, setLocked] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [authRefreshToken, setAuthRefreshToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<ReadonlyArray<string>>([]);
  const [dynamicSubscriptions, setDynamicSubscriptions] = useState<ChannelSubscriptionTable>(EmptyObject as ChannelSubscriptionTable);
  const [analyticsId, setAnalyticsId] = useState<string | null>(null);
  const [currentLoadingStage, setCurrentLoadingStage] = useState<LoadingState>("loadAvailableCoreModules");
  const [coreChannelSubscriptions, setCoreChannelSubscriptions] = useState<ChannelSubscriptionProvider | null>(
    () => DefaultProviderFunction,
  );
  const [appChannelSubscriptions, setAppChannelSubscriptions] = useState<ChannelSubscriptionProvider | null>(() => DefaultProviderFunction);
  const [hasError, setHasError] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const cancelLoadRef = useRef<(() => void) | null>(null);

  const onLoadingStateChange = useEvent(props.onLoadingStateChange);
  const onAnalyticsIdChange = useEvent(props.onAnalyticsIdChange);

  const loadFromStage = useCallback((stage: LoadingState) => {
    let cancel = false;
    let failed = false;

    async function waitForLoadingToComplete() {
      await new Promise<void>((resolve) => {
        function doCheck() {
          if (loadingCountRef.current === 0) {
            resolve();
          } else setTimeout(doCheck);
        }

        doCheck();
      });
    }

    /** Initializes the core modules. */
    async function initializeCoreModules() {
      try {
        const [newModules, newChannelSubscriptions] = await loadModules("core", coreModulePaths.current, null);
        if (cancel) return;

        coreModules.current = newModules;
        setCoreChannelSubscriptions(() => newChannelSubscriptions);
      } catch {
        setHasError(true);
      }
    }

    /** Initializes the app modules. */
    async function initializeAppModules() {
      const permissionsToUse = deferredPermissions.current ? deferredPermissions.current : permissions;

      // Clear whatever deferred permissions we have right now.
      deferredPermissions.current = null;

      const [newModules, newChannelSubscriptions] = await loadModules("app", appModulePaths.current, permissionsToUse);
      if (cancel) return;

      appModules.current = newModules;
      setAppChannelSubscriptions(() => newChannelSubscriptions);
    }

    async function runStage(stage: LoadingState): Promise<void> {
      await waitForLoadingToComplete();
      if (cancel) return;

      console.log(`[Shell] ${LoadingStateNames[stage]}`);

      switch (stage) {
        case "loadAvailableCoreModules":
          coreModulePaths.current = await fetchAvailableModules("core");
          /* Without the core manifest there is no login module, so fail visibly rather than render an empty shell. */
          if (coreModulePaths.current === null) failed = true;
          break;
        case "loadAvailableAppModules":
          appModulePaths.current = await fetchAvailableModules("app");
          /* An unreadable app manifest leaves the shell with no modules, so fail visibly rather than render empty chrome. */
          if (appModulePaths.current === null) failed = true;
          break;
        case "clearCoreModules":
          clearFederationRuntimeCache(coreModulePaths.current);
          coreModules.current = null;
          break;
        case "initializeCoreModules":
          await initializeCoreModules();
          break;
        case "clearAppModules":
          /* Containers are keyed by scope and remote entry and are safe to reuse. Tearing them
             down here races the previous run's in-flight loads and duplicates module state. */
          appModules.current = null;
          break;
        case "initializeAppModules":
          await initializeAppModules();
          break;
        case "renderRoutes":
          break;
        default:
          break;
      }
    }

    async function run() {
      let currentStage: LoadingState | null = stage;

      while (currentStage !== null) {
        await runStage(currentStage);
        if (cancel) return;

        if (failed) {
          setHasError(true);
          return;
        }

        setCurrentLoadingStage(currentStage);
        onLoadingStateChange(currentStage);

        if (deferredPermissions.current && currentStage === "renderRoutes") currentStage = "clearAppModules";
        else currentStage = LifeCycleProcess[currentStage];
      }
    }

    run().catch((err) => {
      console.error("[Shell] Module loading failed.", err);
      setHasError(true);
    });

    return () => {
      cancel = true;
    };
  }, []);

  /* If permissions change and we're already rendering the routes, move back to clearAppModules.
     Skip this during logout (when authToken becomes null) to prevent race conditions. */
  useEffect(() => {
    /* Guard: Don't trigger module reload when logging out (authToken is null)
       This prevents a race condition where clearing permissions triggers clearAppModules
       while the logout navigation is still in progress.
       Note: authToken and isLoggingOut are intentionally NOT in the dependency array
       because we only want this effect to run on permissions changes, not auth state changes. */
    if (authToken === null || isLoggingOut) {
      return;
    }

    deferredPermissions.current = permissions;

    if (currentLoadingStage === "renderRoutes") {
      if (cancelLoadRef.current) {
        cancelLoadRef.current();
        cancelLoadRef.current = null;
      }

      cancelLoadRef.current = loadFromStage("clearAppModules");

      return () => {
        if (!cancelLoadRef.current) return;
        cancelLoadRef.current();
        cancelLoadRef.current = null;
      };
    }
  }, [permissions]);

  // Reset isLoggingOut flag when a new auth token is set (successful login after logout)
  useEffect(() => {
    if (authToken !== null && isLoggingOut) {
      setIsLoggingOut(false);
    }
  }, [authToken, isLoggingOut]);

  useEffect(() => {
    if (authToken === null) {
      setAnalyticsId(null);
      return;
    }

    const parts = authToken.split(".");
    const payload = JSON.parse(atob(parts[1]));
    setAnalyticsId(payload.username);
  }, [authToken]);

  useEffect(() => {
    if (cancelLoadRef.current) {
      cancelLoadRef.current();
      cancelLoadRef.current = null;
    }

    cancelLoadRef.current = loadFromStage("loadAvailableCoreModules");

    return () => {
      if (!cancelLoadRef.current) return;
      cancelLoadRef.current();
      cancelLoadRef.current = null;
    };
  }, []);

  useEffect(() => {
    onAnalyticsIdChange(analyticsId);
  }, [props.onAnalyticsIdChange, analyticsId]);

  const incrementLoadingCountHandler = useCallback((): void => {
    if (loadingCountRef.current === 0) {
      console.log("[Shell] Life Cycle Locked, Waiting...");
      setLocked(true);
    }

    loadingCountRef.current++;
  }, []);

  const decrementLoadingCountHandler = useCallback((): void => {
    if (loadingCountRef.current === 0) return;
    loadingCountRef.current--;

    if (loadingCountRef.current === 0) {
      console.log("[Shell] Life Cycle Unlocked, Resuming");
      setLocked(false);
    }
  }, []);

  const modules = useMemo(() => [...(appModules.current || []), ...(coreModules.current || [])], [coreModules.current, appModules.current]);
  const rootModule = useMemo(() => (modules ? modules.filter((module) => !!module.index)[0] || null : null), [modules]);

  const registerDynamicChannelSubscription = useCallback(
    (moduleId: string | null, channelId: string, subscription: ComponentSubscription): DynamicSubscriptionDeregistrationFunc => {
      if (moduleId === null) moduleId = "_null_";
      const storedModuleId = moduleId === null ? "_null_" : moduleId;

      const { module: info, component: Component } = subscription;
      const module = modules.find((module) => module.info === info);
      if (!module) {
        return (): void => {
          /* do nothing */
        };
      }

      const containerProvider = async () => {
        switch (module.version) {
          case 1:
          case undefined:
            return module.container;
          case 2:
            return await module.container?.();
          default:
            return undefined;
        }
      };

      const wrappedComponent = (): JSX.Element => (
        <OwnerModuleContext.Provider value={info}>
          <AsyncWrapper resolver={containerProvider}>
            <Component />
          </AsyncWrapper>
        </OwnerModuleContext.Provider>
      );

      const finalSubscription = {
        ...subscription,
        component: wrappedComponent,
      };

      setDynamicSubscriptions((prev) => ({
        ...prev,
        [storedModuleId]: {
          ...prev?.[storedModuleId],
          [channelId]: [...(prev?.[storedModuleId]?.[channelId] || []), finalSubscription],
        },
      }));

      return (): void => {
        setDynamicSubscriptions((prev) => ({
          ...prev,
          [storedModuleId]: {
            ...prev?.[storedModuleId],
            [channelId]: prev?.[storedModuleId]?.[channelId]?.filter((x) => x !== finalSubscription),
          },
        }));
      };
    },
    [modules],
  );

  const getDynamicChannelSubscriptions = useCallback(
    (moduleId: string | null, channelId: string): ReadonlyArray<Subscription> => {
      if (moduleId === null) moduleId = "_null_";

      const channels = dynamicSubscriptions[moduleId];
      if (!channels) return EmptyArray as ReadonlyArray<Subscription>;
      return channels[channelId] || (EmptyArray as ReadonlyArray<Subscription>);
    },
    [dynamicSubscriptions],
  );

  const authContextValue = useMemo<AuthContextValue>(() => {
    return {
      authToken,
      authRefreshToken,
      setAuthToken,
      setAuthRefreshToken,
      permissions,
      setPermissions,
    };
  }, [authToken, authRefreshToken, permissions]);

  const notifyLogoutStartedHandler = useCallback((): void => {
    setIsLoggingOut(true);
    // Cancel any in-progress module loading operations
    if (cancelLoadRef.current) {
      cancelLoadRef.current();
      cancelLoadRef.current = null;
    }
  }, []);

  const shellContextValue = useMemo<ShellContextModel>(() => {
    let trimmedPath = path;
    if (path.endsWith("/")) trimmedPath = path.substr(0, path.length - 1);

    return {
      rootPath: path,
      moduleRootPath: `${trimmedPath}/module`,
      rootModule: rootModule ? rootModule.info || null : null,
      modules,
      incrementLoadingCount: incrementLoadingCountHandler,
      decrementLoadingCount: decrementLoadingCountHandler,
      getChannelSubscriptions: (moduleId, channelId): ReadonlyArray<Subscription> => {
        const staticSubscriptions = [
          ...(appChannelSubscriptions ? appChannelSubscriptions(moduleId, channelId) : []),
          ...(coreChannelSubscriptions ? coreChannelSubscriptions(moduleId, channelId) : []),
        ];

        // The total set of static subscriptions need to be sorted based on their descending priority.
        staticSubscriptions.sort((a, b) => a.priority - b.priority);

        return [...staticSubscriptions, ...getDynamicChannelSubscriptions(moduleId, channelId)];
      },
      registerDynamicChannelSubscription,
      setAnalyticsId: (analyticsId): void => {
        setAnalyticsId(analyticsId || null);
      },
      isLoggingOut,
      notifyLogoutStarted: notifyLogoutStartedHandler,
    };
  }, [
    path,
    modules,
    rootModule,
    coreChannelSubscriptions,
    appChannelSubscriptions,
    getDynamicChannelSubscriptions,
    registerDynamicChannelSubscription,
    incrementLoadingCountHandler,
    decrementLoadingCountHandler,
    setAnalyticsId,
    isLoggingOut,
    notifyLogoutStartedHandler,
  ]);

  return (
    <TooltipContainer>
      <OverlayContainer>
        <HotkeyRegistryContext.Provider value={hotkeyRegistryManager}>
          <ShellHotkeyWrapper>
            <Body>
              <AuthContext.Provider value={authContextValue}>
                <AuthTokenProvider authToken={authToken}>
                  <ShellContext.Provider value={shellContextValue}>
                    {!hasError && (
                      <AlertProvider>
                        <div className={classNames(styles.lifeCycleIndicator, "life-cycle")}>{currentLoadingStage}</div>
                        {currentLoadingStage !== "renderRoutes" ? (
                          <Shell.Splash locked={locked} loadingState={currentLoadingStage} />
                        ) : (
                          <Routes>{children}</Routes>
                        )}
                        <ContentChannel id="global" />
                      </AlertProvider>
                    )}
                    {hasError && (
                      <ContentLayout>
                        <ErrorPage />
                      </ContentLayout>
                    )}
                  </ShellContext.Provider>
                </AuthTokenProvider>
              </AuthContext.Provider>
            </Body>
          </ShellHotkeyWrapper>
        </HotkeyRegistryContext.Provider>
      </OverlayContainer>
    </TooltipContainer>
  );
};

/** The `Shell.Splash` component properties. */
interface SplashProps {
  /** The current shell loading state. */
  readonly loadingState: LoadingState;

  /** Indicates whether shell loading currently locked. */
  readonly locked: boolean;
}

/** The shell splash screen. */
Shell.Splash = (props: SplashProps): ReactNode => {
  const { loadingState, locked } = props;

  return (
    <div className={styles.splashRoot}>
      <div className={styles.splashContent}>
        <div className={styles.splashSpinner}>
          <div className={styles.dot1} />
          <div className={styles.dot2} />
        </div>

        {loadingState !== "renderRoutes" && process.env.NODE_ENV !== "production" && (
          <div className={classNames(styles.loadingStatus, "loading-status", { [styles.locked]: locked }, styles[loadingState])} />
        )}
      </div>
    </div>
  );
};
